/**
 * Event Landing Page Service
 * CRUD operations for event landing pages
 */

import { supabase } from "./supabase";
import { fetchAllRows, pageByNameThenId } from "../utils/fetchAllRows";
import type { UUID } from "../types/common.types";
import type {
  EventLandingPage,
  CreateLandingPageInput,
  UpdateLandingPageInput,
} from "../types/eventLandingPage.types";

// =====================================================
// CRUD OPERATIONS
// =====================================================

/**
 * Get landing page for an event
 */
export async function getLandingPageByEventId(
  eventId: UUID,
): Promise<EventLandingPage | null> {
  const { data, error } = await supabase
    .from("event_landing_pages")
    .select("*")
    .eq("event_id", eventId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching landing page:", error);
    throw error;
  }

  return data;
}

/**
 * Get landing page by slug (for public page)
 */
export async function getLandingPageBySlug(slug: string): Promise<
  | (EventLandingPage & {
      events: {
        id: string;
        title: string;
        description: string | null;
        start_time: string;
        end_time: string | null;
        timezone: string | null;
        location_name: string | null;
        location_address: unknown;
        status: string;
        capacity: number | null;
        max_attendees: number | null;
        registration_open: boolean | null;
        registration_deadline: string | null;
        is_public: boolean;
      };
    })
  | null
> {
  const { data, error } = await supabase
    .from("event_landing_pages")
    .select(
      `*,
      events!inner(
        id, title, description, start_time, end_time, timezone,
        location_name, location_address, status,
        capacity, max_attendees, registration_open, registration_deadline, is_public
      )`,
    )
    .eq("slug", slug)
    .maybeSingle();

  if (error) {
    console.error("Error fetching landing page by slug:", error);
    throw error;
  }

  return data;
}

/**
 * Create a new landing page
 */
export async function createLandingPage(
  input: CreateLandingPageInput,
  userId: UUID,
): Promise<EventLandingPage> {
  const { data, error } = await supabase
    .from("event_landing_pages")
    .insert({
      ...input,
      created_by: userId,
      updated_by: userId,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating landing page:", error);
    throw error;
  }

  return data;
}

/**
 * Update a landing page
 */
export async function updateLandingPage(
  id: UUID,
  updates: UpdateLandingPageInput,
  userId: UUID,
): Promise<EventLandingPage> {
  const { data, error } = await supabase
    .from("event_landing_pages")
    .update({
      ...updates,
      updated_by: userId,
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating landing page:", error);
    throw error;
  }

  return data;
}

/**
 * Delete a landing page
 */
export async function deleteLandingPage(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("event_landing_pages")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting landing page:", error);
    throw error;
  }
}

/**
 * Set published state explicitly (Publish / Unpublish).
 */
export async function setLandingPagePublished(
  id: UUID,
  isPublished: boolean,
  userId: UUID,
): Promise<EventLandingPage> {
  return updateLandingPage(id, { is_published: isPublished }, userId);
}

// =====================================================
// REGISTRATION LIST (event_list_associations, type 'registered')
// =====================================================
// We use event_list_associations (which has association_type) rather than the
// generic event_lists junction (which has NO association_type column — verified in
// QA+PROD), so the registration list is a typed, distinct association.

const REGISTERED = "registered";

export interface RegistrationListInfo {
  list_id: UUID;
  name: string;
  is_dynamic: boolean;
  contact_count: number | null;
}

/** The static CRM list registrants are added to for this event, or null. */
export async function getRegistrationList(
  eventId: UUID,
): Promise<RegistrationListInfo | null> {
  const { data, error } = await supabase
    .from("event_list_associations")
    .select("list_id, lists!inner(id, name, is_dynamic, contact_count)")
    .eq("event_id", eventId)
    .eq("association_type", REGISTERED)
    .maybeSingle();
  if (error) {
    console.error("Error fetching registration list:", error);
    throw error;
  }
  if (!data) {
    return null;
  }
  const l = data.lists as unknown as {
    name: string;
    is_dynamic: boolean;
    contact_count: number | null;
  };
  return {
    list_id: data.list_id,
    name: l.name,
    is_dynamic: l.is_dynamic,
    contact_count: l.contact_count,
  };
}

/**
 * Point this event's registration list at an existing static list (replaces any prior).
 * Insert the new association FIRST, then remove other 'registered' rows — so a failed
 * insert never leaves the event with no registration list (the unique key is
 * (event_id, list_id, association_type), so re-selecting the same list is a no-op upsert).
 */
export async function setRegistrationList(
  eventId: UUID,
  listId: UUID,
  userId: UUID,
): Promise<void> {
  const { error } = await supabase.from("event_list_associations").upsert(
    {
      event_id: eventId,
      list_id: listId,
      association_type: REGISTERED,
      created_by: userId,
    },
    { onConflict: "event_id,list_id,association_type", ignoreDuplicates: true },
  );
  if (error) {
    console.error("Error setting registration list:", error);
    throw error;
  }
  // Only now drop any prior 'registered' associations pointing at a different list.
  const { error: delErr } = await supabase
    .from("event_list_associations")
    .delete()
    .eq("event_id", eventId)
    .eq("association_type", REGISTERED)
    .neq("list_id", listId);
  if (delErr) {
    console.error("Error pruning old registration lists:", delErr);
    throw delErr;
  }
}

/** Remove the registration-list association (does not delete the list itself). */
export async function clearRegistrationList(eventId: UUID): Promise<void> {
  const { error } = await supabase
    .from("event_list_associations")
    .delete()
    .eq("event_id", eventId)
    .eq("association_type", REGISTERED);
  if (error) {
    console.error("Error clearing registration list:", error);
    throw error;
  }
}

/** Create a new STATIC list and associate it as this event's registration list. */
export async function createRegistrationList(
  eventId: UUID,
  name: string,
  userId: UUID,
): Promise<RegistrationListInfo> {
  const { data, error } = await supabase
    .from("lists")
    .insert({
      name,
      is_dynamic: false,
      list_type: "static",
      is_active: true,
      event_id: eventId,
      created_by: userId,
      updated_by: userId,
    })
    .select("id, name, is_dynamic, contact_count")
    .single();
  if (error) {
    console.error("Error creating registration list:", error);
    throw error;
  }
  await setRegistrationList(eventId, data.id, userId);
  return {
    list_id: data.id,
    name: data.name,
    is_dynamic: data.is_dynamic,
    contact_count: data.contact_count,
  };
}

/** Existing static lists, for the selector. Dynamic lists are excluded (can't take rows). */
export async function listStaticLists(): Promise<
  Array<{ id: UUID; name: string; contact_count: number | null }>
> {
  // Paged: static lists alone are near PostgREST's 1000-row cap on PROD.
  try {
    return await fetchAllRows<{
      id: UUID;
      name: string;
      contact_count: number | null;
    }>((from, to) =>
      pageByNameThenId(
        supabase
          .from("lists")
          .select("id, name, contact_count")
          .eq("is_dynamic", false)
          .eq("is_active", true)
          .is("archived_at", null),
        from,
        to,
      ),
    );
  } catch (error) {
    console.error("Error listing static lists:", error);
    throw error;
  }
}

/**
 * Lists selectable as an Event-in-a-Box invitation audience: active STATIC and DYNAMIC
 * (rule-based) segments. Unlike listStaticLists, dynamic segments are included — EIB
 * snapshots the chosen list to a frozen static membership at launch (createCombinedList),
 * so a rule-based segment is a valid source even though it can't take direct row inserts.
 * This is what enables "complex selection criteria" audiences (and online / no-venue events,
 * where proximity is meaningless). `is_dynamic` lets the picker label + count them correctly.
 */
export async function listEibAudienceLists(): Promise<
  Array<{
    id: UUID;
    name: string;
    contact_count: number | null;
    is_dynamic: boolean;
  }>
> {
  // Paged: PROD has more active segments than PostgREST's 1000-row cap, so a
  // single select hid every audience past ~#1000 by name from the EIB picker.
  let data: Array<Record<string, unknown>>;
  try {
    data = await fetchAllRows<Record<string, unknown>>((from, to) =>
      pageByNameThenId(
        supabase
          .from("lists")
          .select("id, name, contact_count, is_dynamic")
          .eq("is_active", true)
          .is("archived_at", null),
        from,
        to,
      ),
    );
  } catch (error) {
    console.error("Error listing EIB audience lists:", error);
    throw error;
  }
  return data.map((l) => ({
    id: l.id as UUID,
    name: l.name as string,
    contact_count: (l.contact_count as number | null) ?? null,
    is_dynamic: !!l.is_dynamic,
  }));
}

// =====================================================
// SLUG UTILITIES
// =====================================================

/**
 * Generate a URL-safe slug from event title
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 200);
}

/**
 * Check if a slug is available
 */
export async function isSlugAvailable(
  slug: string,
  excludeId?: UUID,
): Promise<boolean> {
  let query = supabase
    .from("event_landing_pages")
    .select("id")
    .eq("slug", slug);

  if (excludeId) {
    query = query.neq("id", excludeId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error("Error checking slug:", error);
    throw error;
  }

  return data === null;
}

// =====================================================
// TEMPLATE PARSING
// =====================================================

const FIELD_NAMES = new Set([
  "firstName",
  "lastName",
  "email",
  "phone",
  "company",
  "addressLine1",
  "city",
  "state",
  "postalCode",
  "country",
  "howDidYouHear",
  "messagingConsent",
  "discountCode",
]);

const DISPLAY_TAGS = new Set(["memberStatus", "memberPrice", "nonMemberPrice"]);

/**
 * Parse template tags from HTML and return field config derived from the HTML
 */
export function parseTemplateFields(
  html: string,
): Record<string, { required: boolean }> {
  const tagRegex = /\{\{(\w+)(\*?)\}\}/g;
  const fields: Record<string, { required: boolean }> = {};
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    const fieldName = match[1];
    const isRequired = match[2] === "*";

    if (FIELD_NAMES.has(fieldName) || DISPLAY_TAGS.has(fieldName)) {
      fields[fieldName] = { required: isRequired };
    }
  }

  return fields;
}
